import { Badge } from "primereact/badge";
import { Button } from "primereact/button";
import { Message } from "primereact/message";
import { InputSwitch } from "primereact/inputswitch";
import { Tag } from "primereact/tag";

import { EmptyState, ErrorState, ForbiddenState, Icon, LoadingState } from "@/ds";
import {
  BASE_DADOS_FORBIDDEN_MESSAGE,
  BASE_DADOS_LOADING_LABEL,
  BASE_DADOS_MENU_LABEL,
  BASE_DADOS_MOSTRAR_INATIVOS_LABEL,
  BASE_DADOS_NOVO_GRUPO_LABEL,
  BASE_DADOS_TITULO,
  BASE_DADOS_VAZIO_DESCRICAO,
  BASE_DADOS_VAZIO_TITULO,
} from "./base-dados.constants";
import { InativarAtivarReferencia } from "./components/inativar-ativar-referencia";
import { NovoGrupoDialog } from "./components/novo-grupo-dialog";
import styles from "./base-dados.module.scss";
import type { BaseDadosViewModel } from "./types-base-dados";

const INATIVOS_INPUT_ID = "base-dados-mostrar-inativos";
const COLUNA_SITUACAO = "situacao";

function ariaSort(ordenacao: BaseDadosViewModel["ordenacao"], campo: string) {
  if (ordenacao?.campo !== campo) return "none";
  return ordenacao.direcao === "asc" ? "ascending" : "descending";
}

export function BaseDadosView({
  conteudo,
  erro,
  menu,
  categoria,
  descricao,
  aviso,
  colunas,
  linhas,
  ordenacao,
  ordenarPor,
  filtraInativos,
  mostrarInativos,
  alternarInativos,
  recarregar,
  novoGrupo,
  novoGrupoDesabilitado,
  tiposObra,
  acaoPendente,
  fecharAcao,
  concluirAcao,
}: BaseDadosViewModel) {
  return (
    <section
      className={styles.tela}
      aria-label={BASE_DADOS_TITULO}
      data-testid="financeiro-base-dados-screen"
    >
      <header className={styles.secao}>
        <h2 className={styles.titulo}>{BASE_DADOS_TITULO}</h2>
        <p className={styles.descricao}>{descricao}</p>
      </header>

      <div className={styles.corpo}>
        <nav className={styles.menu} aria-label={BASE_DADOS_MENU_LABEL}>
          {menu.map((item) => (
            <button
              key={item.categoria}
              type="button"
              className={item.ativo ? styles.itemAtivo : styles.item}
              aria-current={item.ativo ? "true" : undefined}
              onClick={item.selecionar}
              data-testid={item.testId}
            >
              <span className={styles.itemRotulo}>{item.rotulo}</span>
              {item.contador !== null ? <Badge value={item.contador} severity="secondary" /> : null}
            </button>
          ))}
        </nav>

        <div className={styles.lista}>
          {filtraInativos || novoGrupo ? (
            <div className={styles.barra}>
              {filtraInativos ? (
                <div className={styles.inativos}>
                  <InputSwitch
                    inputId={INATIVOS_INPUT_ID}
                    checked={mostrarInativos}
                    onChange={alternarInativos}
                    data-testid="base-dados-mostrar-inativos"
                  />
                  <label htmlFor={INATIVOS_INPUT_ID}>{BASE_DADOS_MOSTRAR_INATIVOS_LABEL}</label>
                </div>
              ) : null}
              {novoGrupo ? (
                <Button
                  type="button"
                  icon={<Icon name="plus" size={14} />}
                  label={BASE_DADOS_NOVO_GRUPO_LABEL}
                  disabled={novoGrupoDesabilitado}
                  onClick={novoGrupo}
                  data-testid="base-dados-novo-grupo"
                />
              ) : null}
            </div>
          ) : null}

          {aviso ? <Message severity="info" text={aviso} data-testid="base-dados-aviso" /> : null}

          {conteudo === "carregando" ? (
            <LoadingState label={BASE_DADOS_LOADING_LABEL} testId="base-dados-loading" />
          ) : null}
          {conteudo === "sem-permissao" ? (
            <ForbiddenState
              description={BASE_DADOS_FORBIDDEN_MESSAGE}
              testId="base-dados-sem-permissao"
            />
          ) : null}
          {conteudo === "erro" ? (
            <ErrorState message={erro ?? undefined} onRetry={recarregar} testId="base-dados-erro" />
          ) : null}
          {conteudo === "vazio" ? (
            <EmptyState.Root size="sm" testId="base-dados-vazio">
              <EmptyState.Illustration name="sem-dados" />
              <EmptyState.Title>{BASE_DADOS_VAZIO_TITULO}</EmptyState.Title>
              <EmptyState.Description>{BASE_DADOS_VAZIO_DESCRICAO}</EmptyState.Description>
            </EmptyState.Root>
          ) : null}

          {conteudo === "lista" ? (
            <div className={styles.tabelaCartao}>
              <table
                className={`${styles.tabela} ${categoria === "ESTRUTURA" ? styles.tabelaEstrutura : styles.tabelaTipos}`}
                aria-label={BASE_DADOS_MENU_LABEL}
                data-testid="base-dados-tabela"
              >
                <thead>
                  <tr>
                    {colunas.map(({ campo, rotulo }) => (
                      <th key={campo} scope="col" aria-sort={ariaSort(ordenacao, campo)}>
                        <button
                          type="button"
                          className={styles.cabecalho}
                          onClick={() => ordenarPor(campo)}
                        >
                          {rotulo}
                          {ordenacao?.campo === campo ? (
                            <Icon
                              name={
                                ordenacao.direcao === "asc"
                                  ? "sort-amount-up-alt"
                                  : "sort-amount-down"
                              }
                              size={14}
                            />
                          ) : null}
                        </button>
                      </th>
                    ))}
                    <th className={styles.colunaAcoes} aria-hidden="true" />
                  </tr>
                </thead>
                <tbody>
                  {linhas.map((linha) => (
                    <tr key={linha.id}>
                      {colunas.map(({ campo }) => (
                        <td key={campo}>
                          {campo === COLUNA_SITUACAO ? (
                            <Tag
                              value={linha.situacao}
                              severity={linha.inativa ? "secondary" : "success"}
                            />
                          ) : (
                            linha.celulas[campo]
                          )}
                        </td>
                      ))}
                      <td className={styles.colunaAcoes}>
                        {linha.acao ? (
                          <Button
                            type="button"
                            icon={<Icon name="power-off" size={14} />}
                            aria-label={linha.acao.label}
                            aria-haspopup="dialog"
                            onClick={linha.acao.executar}
                            severity="secondary"
                            text
                            data-testid={linha.acao.testId}
                          />
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      </div>

      {acaoPendente?.tipo === "novo-grupo" ? (
        <NovoGrupoDialog tiposObra={tiposObra} onClose={fecharAcao} onConcluido={concluirAcao} />
      ) : null}
      {acaoPendente && acaoPendente.tipo !== "novo-grupo" ? (
        <InativarAtivarReferencia
          acao={acaoPendente}
          onClose={fecharAcao}
          onConcluido={concluirAcao}
        />
      ) : null}
    </section>
  );
}
